import Box from '@mui/material/Box';
import { useAccountSelectionContext } from './AccountSelectionContext';
import { BaseDialog } from '../common/dialogs/BaseDialog';
import { DialogContent, Stack } from '@mui/material';
import { Button, colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { SearchInput } from '../common/SearchInput';
import pluralize from 'pluralize';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUserInfoData } from '../../reducers/userInfoReducer';
import { UserAccount } from '@server/user-account';
import { Check, Close } from '@sweep-io/sweep-design/dist/icons';
import useLogout from '../../auth/useLogout';
import { useRunOnceWhenTruthy } from '../common/useRunOnceWhenTruthy';
import { AccountActionMenu } from './AccountActionMenu';
import { useNavigate } from 'react-router';
import { appRoutes } from '../../constants/appRoutes';
import { createAccountUrl } from './utils';

export const WithAccountsSelectionDialog = ({
  children,
  isBlockingAccountSelection, // If true, user must select an account to continue using Sweep.
  onCreateNewAccountClick,
}: {
  children: React.ReactNode;
  isBlockingAccountSelection?: boolean;
  onCreateNewAccountClick?: () => void;
}) => {
  const userInfo = useSelector(selectUserInfoData);
  const navigate = useNavigate();
  const { isOpen, closeAccountSelection, accounts, isLoadingAccounts, fetchAccounts } =
    useAccountSelectionContext();

  const { logout } = useLogout();

  const currentUserAccount = userInfo?.account?.id;
  const userAccount = useMemo(() => {
    return userInfo?.account
      ? ({
          id: userInfo.account.id,
          name: userInfo.account.name,
          authZeroOrgId: userInfo.account.authZeroOrgId,
          deactivatable: false,
        } as UserAccount)
      : null;
  }, [userInfo]);

  const [chosenAccount, setChosenAccount] = useState<UserAccount | null>(userAccount);
  const [searchText, setSearchText] = useState('');

  const _isOpen = isBlockingAccountSelection || isOpen;

  const filteredAccounts = useMemo(() => {
    return accounts?.filter((account) =>
      account?.name?.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [accounts, searchText]);

  const isEmptyAccounts = filteredAccounts?.length === 0;

  useRunOnceWhenTruthy(fetchAccounts, !accounts);

  const handleConfirmSelection = useCallback((_chosenAccount: UserAccount) => {
    if (_chosenAccount?.authZeroOrgId) {
      window.location.href = createAccountUrl(_chosenAccount?.authZeroOrgId);
    } else {
      console.error('No auth zero org id found for chosen account', _chosenAccount?.id);
    }
  }, []);

  const isSelectedAccount = useCallback(
    (account: UserAccount) => {
      return account.id === chosenAccount?.id;
    },
    [chosenAccount],
  );

  const onClose = useCallback(() => {
    closeAccountSelection();
    setChosenAccount(userAccount);
  }, [closeAccountSelection, userAccount]);

  return (
    <Box width="100%" height="100%">
      <BaseDialog onClose={onClose} open={_isOpen}>
        <Stack
          sx={{ padding: '24px', pb: 2, width: '600px' }}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack gap={0.5}>
            <Typography variant="h3">Choose an account</Typography>
            {isBlockingAccountSelection && (
              <Typography variant="body" color={colors.grey[700]}>
                Select an account to continue using Sweep.
              </Typography>
            )}
          </Stack>

          {!isBlockingAccountSelection && (
            <IconButton size="small" variant="flat" onClick={onClose}>
              <Close />
            </IconButton>
          )}
        </Stack>

        <DialogContent
          sx={{
            padding: '24px',
            pt: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            sx={{
              padding: '8px',
              flexDirection: 'row',
              justifyContent: 'space-between',
              backgroundColor: colors.grey[100],
              borderRadius: '8px',
            }}
          >
            <SearchInput
              TextFieldProps={{ sx: { width: '388px' } }}
              placeholder="Search for an account"
              withFixedMagnifyingGlassIcon
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />

            <Stack sx={{ '& button': { whiteSpace: 'nowrap' }, justifyContent: 'center' }}>
              <Button
                variant="system"
                size="small"
                startIconName="Plus"
                disabled={isLoadingAccounts}
                onClick={() => {
                  onCreateNewAccountClick?.() ?? navigate(appRoutes.createAccount.route);
                  closeAccountSelection();
                }}
              >
                New Account
              </Button>
            </Stack>
          </Stack>

          <Stack sx={{ padding: '8px', pt: 2, pb: 2 }}>
            <Typography variant="caption" color={colors.grey[500]}>
              {pluralize('Account', filteredAccounts?.length || 0, true)}
            </Typography>
          </Stack>

          <Stack
            sx={{
              flexDirection: 'column',
              gap: 0.5,
              height: '280px',
              overflow: 'auto',
            }}
          >
            {isLoadingAccounts && <CenteredCircularProgress />}

            {!isLoadingAccounts && isEmptyAccounts && (
              <Stack alignItems="center" justifyContent="center" height="100%">
                <Typography variant="body" color={colors.grey[500]}>
                  No accounts found
                </Typography>
              </Stack>
            )}

            {!isEmptyAccounts &&
              !isLoadingAccounts &&
              filteredAccounts?.map((account) => (
                <Stack
                  key={account.id}
                  onClick={() => {
                    setChosenAccount(account);

                    //by product definition 'Continue' button should only appear when in blocking mode
                    if (!isBlockingAccountSelection) {
                      handleConfirmSelection(account);
                    }
                  }}
                  sx={{
                    cursor: isSelectedAccount(account) ? 'default' : 'pointer',
                    p: '4px 8px',
                    borderRadius: '8px',
                    backgroundColor: isSelectedAccount(account) ? colors.grey[200] : 'transparent',
                    '&:hover': {
                      backgroundColor: isSelectedAccount(account)
                        ? colors.grey[200]
                        : colors.grey[100],
                    },
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: '40px',
                  }}
                >
                  <Typography
                    variant={isSelectedAccount(account) ? 'body-medium' : 'body'}
                    color={colors.grey[800]}
                  >
                    {account.name
                      ? account.name
                      : account.id === currentUserAccount //for newly created account name might not be set when GET /accounts runs
                        ? userInfo?.account?.name
                        : ''}
                  </Typography>

                  <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                    {isSelectedAccount(account) && (
                      <Stack alignItems="center" justifyContent="center" pr={1}>
                        <Check size="medium" />
                      </Stack>
                    )}

                    <AccountActionMenu
                      accountId={account.id ?? ''}
                      isDeletable={account.deactivatable}
                      updateChosenAccountsState={(deletedAccountId: string) => {
                        if (deletedAccountId === chosenAccount?.id) {
                          setChosenAccount(null);
                        }
                      }}
                    />
                  </Stack>
                </Stack>
              ))}
          </Stack>

          {isBlockingAccountSelection && (
            <Stack
              sx={{
                pt: 2,
                flexDirection: 'row',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Button variant="system" size="small" onClick={logout}>
                Log out
              </Button>

              <Button
                size="small"
                disabled={!chosenAccount || chosenAccount.id === currentUserAccount}
                onClick={() => {
                  if (chosenAccount) {
                    handleConfirmSelection(chosenAccount);
                  }
                }}
              >
                Continue
              </Button>
            </Stack>
          )}
        </DialogContent>
      </BaseDialog>

      {children}
    </Box>
  );
};
